(() => {
  const initHeader = (root) => {
    if (!root || root.dataset.bhReady === 'true') return;
    root.dataset.bhReady = 'true';

    const header = root.querySelector('[data-bh-header]');
    const drawer = root.querySelector('[data-bh-drawer]');
    const overlay = root.querySelector('[data-bh-overlay]');
    const navItems = [...root.querySelectorAll('[data-bh-nav-item]')];
    const transparent = root.dataset.transparent === 'true';
    const hideOnScroll = root.dataset.hideOnScroll === 'true';
    const trigger = root.dataset.openTrigger || 'hover';

    // Prestige-style tracking: do not hide for tiny scroll movements.
    let trackingY = Math.max(0, window.scrollY);
    let visible = true;

    const setHeaderHeight = () => {
      if (!header) return;
      root.style.setProperty('--bh-header-height', `${header.offsetHeight}px`);
      document.documentElement.style.setProperty('--bh-header-height', `${header.offsetHeight}px`);
    };

    const hasOpenUI = () => {
      return Boolean(
        drawer?.classList.contains('is-open') ||
        root.querySelector('[data-bh-nav-item].is-open') ||
        root.querySelector('details[open], dialog[open], [aria-expanded="true"]')
      );
    };

    const setVisibility = (nextVisible) => {
      if (!header || nextVisible === visible) return;
      if (!nextVisible && hasOpenUI()) return;
      visible = nextVisible;
      root.classList.toggle('is-hidden', !visible);
      document.documentElement.style.setProperty('--bh-header-is-visible', visible ? '1' : '0');
    };

    const syncSolid = () => {
      if (!header || !transparent) return;

      const shouldBeSolid = window.scrollY > 8 || document.body.classList.contains('bh-lock') || hasOpenUI();
      header.classList.toggle('is-solid', shouldBeSolid);

      /* v12: the transparent colors are rendered inline by Liquid so the very
         first browser paint is already transparent. Once the header needs to
         become solid, remove those inline values and let the normal CSS/theme
         settings take over. Reapply them only when returning to the very top. */
      if (shouldBeSolid) {
        header.style.removeProperty('background');
        header.style.removeProperty('color');
        header.style.removeProperty('border-color');
      } else {
        header.style.setProperty('background', 'transparent');
        header.style.setProperty('color', getComputedStyle(root).getPropertyValue('--bh-transparent-text').trim() || '#ffffff');
        header.style.setProperty('border-color', 'transparent');
      }
    };

    const closeMenus = (except) => {
      navItems.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        item.querySelector('[data-bh-nav-toggle]')?.setAttribute('aria-expanded', 'false');
      });
      syncSolid();
    };

    navItems.forEach((item) => {
      const btn = item.querySelector('[data-bh-nav-toggle]');
      if (!btn) return;

      const dropdown = item.querySelector(':scope > .bh-dropdown');
      let closeTimer = null;

      const cancelClose = () => {
        if (!closeTimer) return;
        window.clearTimeout(closeTimer);
        closeTimer = null;
      };

      const open = () => {
        cancelClose();
        closeMenus(item);
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        setVisibility(true);
        syncSolid();
      };

      const closeNow = () => {
        cancelClose();
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        syncSolid();
      };

      const scheduleClose = () => {
        cancelClose();
        // A short grace period makes diagonal pointer travel into a large
        // editorial mega menu feel like Prestige instead of snapping shut.
        closeTimer = window.setTimeout(closeNow, 420);
      };

      btn.addEventListener('click', (event) => {
        if (trigger === 'hover' && matchMedia('(hover:hover) and (pointer:fine)').matches) return;
        event.preventDefault();
        item.classList.contains('is-open') ? closeNow() : open();
      });

      if (trigger === 'hover') {
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', scheduleClose);
        dropdown?.addEventListener('mouseenter', cancelClose);
        dropdown?.addEventListener('mouseleave', scheduleClose);
        btn.addEventListener('focus', open);

        // Keyboard users can move through every mega-menu link without the
        // panel closing as focus leaves the trigger button.
        item.addEventListener('focusin', cancelClose);
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) scheduleClose();
        });
      }
    });

    const showOverlay = () => {
      if (!overlay) return;
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
      document.body.classList.add('bh-lock');
      setVisibility(true);
      syncSolid();
    };

    const hideOverlay = () => {
      if (!overlay) return;
      overlay.classList.remove('is-visible');
      setTimeout(() => {
        if (!drawer?.classList.contains('is-open')) overlay.hidden = true;
      }, 250);
      document.body.classList.remove('bh-lock');
      syncSolid();
    };

    const openDrawer = () => {
      closeMenus();
      drawer?.classList.add('is-open');
      drawer?.setAttribute('aria-hidden', 'false');
      showOverlay();
    };

    const closeDrawer = () => {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      hideOverlay();
    };

    root.querySelectorAll('[data-bh-open-drawer]').forEach((el) => el.addEventListener('click', openDrawer));
    root.querySelector('[data-bh-close-drawer]')?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', closeDrawer);

    root.querySelectorAll('[data-bh-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.closest('form')?.submit());
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeMenus();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeMenus();
      closeDrawer();
      setVisibility(true);
    });

    window.addEventListener('scroll', () => {
      const y = Math.max(0, window.scrollY);
      syncSolid();

      if (hideOnScroll && header) {
        // Same intent as Prestige: only hide after a meaningful downward movement.
        if (y > trackingY && y - trackingY > 100) {
          setVisibility(false);
          trackingY = y;
        } else if (y < trackingY) {
          trackingY = y;
          setVisibility(true);
        }
      } else {
        setVisibility(true);
      }
    }, { passive: true });

    // Prestige also reveals its hidden header when the pointer returns to the top edge.
    window.addEventListener('pointermove', (event) => {
      if (!hideOnScroll) return;
      if (event.clientY < 100 && matchMedia('(pointer:fine)').matches) {
        setVisibility(true);
        trackingY = Math.max(0, window.scrollY);
      }
    }, { passive: true });

    // Keep transparent/solid state correct when Craft's native search opens/closes.
    const stateObserver = new MutationObserver(() => {
      if (hasOpenUI()) setVisibility(true);
      syncSolid();
    });
    stateObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-expanded', 'class']
    });

    if ('ResizeObserver' in window && header) {
      new ResizeObserver(setHeaderHeight).observe(header);
    }

    setHeaderHeight();
    syncSolid();
  };

  const initAll = () => document.querySelectorAll('[data-brassium-header]').forEach(initHeader);

  // Initialize immediately when the deferred script executes. Waiting for
  // DOMContentLoaded can make the sticky/transparent state visibly snap in
  // after first paint on heavier Shopify pages.
  if (document.readyState === 'loading') {
    initAll();
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => {
    initHeader(event.target.querySelector('[data-brassium-header]'));
  });
})();
